import { ChangeDetectionStrategy, Component } from '@angular/core';
import { Validators, FormBuilder } from '@angular/forms';
import { IDateObject } from 'src/app/analyses/interfaces/analyses.types';
import { MeasurementAddBaseDirective } from 'src/app/body-measurements/directives/measurement-add-base.directive';
import { MeasurementAddBase } from 'src/app/body-measurements/interfaces/measurement-add.interface';
import { inputPattern } from 'src/app/body-measurements/validators/pattern.validator';
import {
  ngbDateFromDate,
  subtractCurrentDateYear,
  objectToDate,
  ngbDateFromISO,
} from 'src/app/shared/util/date-util';
import { MeasurementAddHostService } from '../../measurement-add-host/measurement-add-host.service';

@Component({
  selector: 'app-measurement-add-bmi',
  templateUrl: './measurement-add-bmi.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class MeasurementAddBmiComponent
  extends MeasurementAddBaseDirective
  implements MeasurementAddBase {
  protected isMetric = true;

  protected form = this.fb.group({
    weight: ['', Validators.required], // ADD VALIDATORS
    heightCM: [''],
    heightFT: [''],
    heightIN: [''],
    date: [ngbDateFromDate(new Date()), Validators.required],
  });

  protected now = ngbDateFromDate(new Date());
  protected minDate = ngbDateFromDate(subtractCurrentDateYear(10));

  constructor(
    private fb: FormBuilder,
    private measurementHost: MeasurementAddHostService
  ) {
    super();
  }

  init(): void {
    // Imperial will have two units (ft, in)
    this.isMetric = this.configuration.measurementMetrics[1].units.length === 1;

    super.init();
  }

  onSubmit() {
    if (this.form.invalid) {
      return;
    }

    const { weight, heightCM, heightFT, heightIN, date } = this.form.value;
    const generatedDate = objectToDate(
      (date as Partial<IDateObject>) ?? {}
    ).toISOString();

    let height = [];
    if (this.isMetric) {
      height = [
        {
          id: this.configuration.measurementMetrics[1].id,
          unitId: this.configuration.measurementMetrics[1].units[0].id,
          value: heightCM,
        },
      ];
    } else {
      height = [
        {
          id: this.configuration.measurementMetrics[1].id,
          unitId: this.configuration.measurementMetrics[1].units[0].id,
          value: heightFT,
        },
        {
          id: this.configuration.measurementMetrics[1].id,
          unitId: this.configuration.measurementMetrics[1].units[1].id,
          value: heightIN,
        },
      ];
    }

    return {
      measurements: [
        {
          id: this.configuration.measurementMetrics[0].id,
          unitId: this.configuration.measurementMetrics[0].units[0].id,
          value: weight,
        },
        ...height,
      ],
      generated: generatedDate,
    };
  }

  protected showBfpDetailed() {
    this.measurementHost.changeComponent('bfpManual', this.configuration.id);
  }

  protected updateValidators(): void {
    const unitWeight = this.configuration.measurementMetrics[0].units[0];
    this.form.controls.weight.addValidators([
      Validators.min(unitWeight.min),
      Validators.max(unitWeight.max),
      Validators.pattern(inputPattern(unitWeight.decimalPlaces)),
    ]);

    if (this.isMetric) {
      const unit = this.configuration.measurementMetrics[1].units[0];
      this.form.controls.heightCM.addValidators([
        Validators.required,
        Validators.min(unit.min),
        Validators.max(unit.max),
        Validators.pattern(inputPattern(unit.decimalPlaces)),
      ]);
    } else {
      const unitFT = this.configuration.measurementMetrics[1].units[0];
      this.form.controls.heightFT.addValidators([
        Validators.required,
        Validators.min(unitFT.min),
        Validators.max(unitFT.max),
        Validators.pattern(inputPattern(unitFT.decimalPlaces)),
      ]);
      const unitIN = this.configuration.measurementMetrics[1].units[1];
      this.form.controls.heightIN.addValidators([
        Validators.required,
        Validators.min(unitIN.min),
        Validators.max(unitIN.max),
        Validators.pattern(inputPattern(unitIN.decimalPlaces)),
      ]);
    }
  }

  protected fillForm(): void {
    const measurements = this.detailedResult.measurements;

    if (this.isMetric) {
      this.form.controls.heightCM.patchValue(String(measurements[1].value));
    } else {
      this.form.patchValue({
        heightFT: String(measurements[1].value),
        heightIN: String(measurements[2].value),
      });
    }

    this.form.patchValue({
      weight: String(measurements[0].value),
      date: ngbDateFromISO(this.analysisResult.result.generated),
    });
  }
}
